import Vue from 'vue';
import { eventBus } from '../../events/event-bus';
import axios from 'axios';
import { dataURItoBlob } from '../../utilities/data-uri-to-blob';
import { calculateTaxes } from '../../utilities/tax-calculations';
import { calculateDiscount } from '../../utilities/discount-calculation';
import { $axios } from './axios-instance';

function persistInLocalStorage(key, item) {
    localStorage.setItem(key, JSON.stringify(item));
}

const state = {
    cartItems: [],
    cartSum: 0,
    currentCartItem: {},
    paymentDetails: {},
    shippingMethod: {},
    availablePaymentMethods: {},
    availableShippingCountries: {},
    shoppingCartID: '',
    sessionID: '',
    histogramVisualizationURL: '',
    discountApplied: false,
    discountCodeObj: {},
};

const mutations = {
    SET_CURRENT_CART_ITEM(state, cartItem) {
        state.currentCartItem = cartItem;
    },
    UPDATE_CURRENT_CART_ITEM(state, cartItem) {
        state.currentCartItem = cartItem;
    },
    SET_CART_ITEMS(state, cartItems) {
        Vue.set(state, 'cartItems', [...cartItems]);
        persistInLocalStorage('cartItems', cartItems);
    },
    SET_CART_SUM(state, cartSum) {
        state.cartSum = cartSum;
    },
    SET_PAYMENT_DETAILS(state, paymentDetails) {
        state.paymentDetails = paymentDetails;
        persistInLocalStorage('paymentDetails', paymentDetails);
    },
    SET_SHIPPING_METHOD(state, shippingMethod) {
        state.shippingMethod = shippingMethod;
    },
    SET_AVAILABLE_SHIPPING_COUNTRIES(state, shippingMethods) {
        state.availableShippingCountries = shippingMethods;
        persistInLocalStorage('shippingMethods', shippingMethods);
    },
    SET_AVAILABLE_PAYMENT_METHODS(state, paymentMethods) {
        state.availablePaymentMethods = paymentMethods;
        persistInLocalStorage('paymentMethods', paymentMethods);
    },
    SET_SHOPPING_CART_ID(state, id) {
        state.shoppingCartID = id;
        persistInLocalStorage('shoppingCartID', id);
    },
    SET_SESSION_ID(state, id) {
        state.sessionID = id;
    },
    SET_DISCOUNT_CODE(state, codeObj) {
        state.discountCodeObj = codeObj;
        persistInLocalStorage('discountCode', codeObj);
    },
    SET_DISCOUNT_CODE_APPLIED(state, applied) {
        state.discountApplied = applied;
        persistInLocalStorage('discountApplied', applied);
    },
};

const API_URL = process.env.VUE_APP_FULFILLMENT_API_BASE_URL;

const actions = {
    createCart: async ({ commit, state }, data) => {
        let cartId = JSON.parse(localStorage.getItem('cartId'));
        if (!cartId) {
            const response = await axios.post(`${API_URL}/checkout/cart`);
            cartId = response.data._id;
            localStorage.setItem('cartId', JSON.stringify(cartId));
            commit('SET_SHOPPING_CART_ID', cartId);
        } else {
            try {
                const response = await axios.get(
                    `${API_URL}/checkout/cart/${cartId}`
                );
                let cartItems = response.data.items;
                commit('SET_SHOPPING_CART_ID', cartId);
                commit('SET_CART_ITEMS', cartItems);
            } catch (e) {
                if (e.response.status === 404) {
                    const response = await axios.post(
                        `${API_URL}/checkout/cart`
                    );
                    cartId = response.data._id;
                    localStorage.setItem('cartId', JSON.stringify(cartId));
                    commit('SET_SHOPPING_CART_ID', cartId);
                }
            }
        }

        return cartId;
    },
    clearCart({ commit }) {
        commit('SET_SHOPPING_CART_ID', '');
        commit('SET_CART_ITEMS', []);
        localStorage.removeItem('cartId');
    },
    createCartItem: async ({ commit, state }, data) => {
        let newCartItem = {
            scale: 1,
            quantity: 1,
            acknowledgedRepairInfo: true,
        };
        commit('SET_CURRENT_CART_ITEM', newCartItem);
    },
    updateCurrentCartItem: async ({ commit, state, rootState }, data) => {
        let cartItem = {
            ...state.currentCartItem,
            ...data,
        };
        commit('SET_CURRENT_CART_ITEM', cartItem);
        if (data.scale || data.quantity) {
            eventBus.cartItemPricingInvalid();
        }
        if (data.color) {
            eventBus.openAnalysisTool('Canvas3D');
        }
    },
    moveCurrentCartItemToCart: async ({
        commit,
        state,
        dispatch,
        rootGetters,
    }) => {
        try {
            let cartId = state.shoppingCartID;
            let referencedId = rootGetters.current3DFile._id;
            let givenUnitOfLength = rootGetters.current3DFile.givenUnitOfLength;
            let scale = state.currentCartItem.scale;
            if (cartId === '') {
                cartId = await dispatch('createCart');
            }

            let postData = {
                items: [
                    {
                        ...state.currentCartItem,
                        referencedId,
                        givenUnitOfLength,
                        scale,
                        colorId: state.currentCartItem.color._id,
                        finishId: state.currentCartItem.selectedFinishId,
                    },
                ],
            };
            const response = await axios.post(
                `${API_URL}/checkout/cart/${cartId}/item`,
                postData,
                {
                    headers: {
                        'x-api-key': process.env.VUE_APP_FULLFILLMENT_API_KEY,
                    },
                }
            );
            commit('SET_CART_ITEMS', response.data['cart']['items']);
            commit('SET_CURRENT_CART_ITEM', {
                ...state.currentCartItem,
                _id: response.data['createdItems'][0]['_id'],
            });

            dispatch('postCartItemThumbnail', {
                frontImageData: state.currentCartItem.frontImageData,
            });
        } catch (e) {
            console.log(e);
            throw new Error('Could not move item to cart');
        }
    },
    postCartItemThumbnail: async ({ commit, state }, { frontImageData }) => {
        let cartId = state.shoppingCartID;
        let itemId = state.currentCartItem._id;
        const imageFile = dataURItoBlob(frontImageData);
        const formData = new FormData();

        formData.append('file', imageFile, `${itemId}_thumbnail.png`);
        const response = await axios.post(
            `${API_URL}/checkout/cart/${cartId}/item/${itemId}/thumbnail`,
            formData
        );

        commit('SET_CART_ITEMS', response.data['items']);
        commit('SET_CURRENT_CART_ITEM', {});
        commit('SET_CURRENT_3D_FILE', {});
    },
    uploadCartItemAttachments: async (
        { commit, state, dispatch },
        { id, file }
    ) => {
        let cartId = state.shoppingCartID;
        const formData = new FormData();
        formData.append('file', file, file.name);
        const response = await axios.post(
            `${API_URL}/checkout/cart/${cartId}/item/${id}/attachments`,
            formData
        );
        commit('SET_CART_ITEMS', response.data['items']);
    },
    updateCartItemByID: async ({ commit, state }, data) => {
        let cartId = state.shoppingCartID;
        if (!data.cartItemId || cartId === '') {
            return;
        }

        let cartItems = [...state.cartItems];
        let cartItemIndex = cartItems.findIndex(
            (item) => item._id === data.cartItemId
        );
        let cartItem = cartItems[cartItemIndex];

        if (!cartItem) {
            return;
        }

        cartItems[cartItemIndex] = { ...cartItem, ...data };
        commit('SET_CART_ITEMS', cartItems);
    },
    persistCartItemByID: async ({ commit, state }, data) => {
        let cartId = state.shoppingCartID;
        if (!data.cartItemId || cartId === '') {
            return;
        }
        let cartItem = state.cartItems.find(
            (item) => item._id === data.cartItemId
        );
        if (!cartItem) {
            return;
        }

        let postData = { items: [cartItem] };
        const response = await axios.patch(
            `${API_URL}/checkout/cart/${cartId}/item/${data.cartItemId}`,
            postData,
            {
                headers: {
                    'x-api-key': process.env.VUE_APP_FULLFILLMENT_API_KEY,
                },
            }
        );

        commit('SET_CART_ITEMS', response.data['items']);
    },
    deleteCartItemByID: async ({ commit, state }, data) => {
        let cartId = state.shoppingCartID;
        if (!data.cartItemId || cartId === '') {
            return;
        }
        const response = await axios.delete(
            `${API_URL}/checkout/cart/${cartId}/item/${data.cartItemId}`
        );

        commit('SET_CART_ITEMS', response.data['items']);
    },
    validateDiscountCode: async ({ commit, state }, code) => {
        commit('SET_DISCOUNT_CODE', {});
        const discount = (
            await axios.post(
                `${API_URL}/checkout/order/validate-discount`,
                code,
                {
                    headers: {
                        'x-api-key': process.env.VUE_APP_FULLFILLMENT_API_KEY,
                    },
                }
            )
        ).data;
        commit('SET_DISCOUNT_CODE', discount);
        commit('SET_DISCOUNT_CODE_APPLIED', false);
        return discount;
    },
    discardDiscount: async ({ commit }) => {
        commit('SET_DISCOUNT_CODE', {});
        commit('SET_DISCOUNT_CODE_APPLIED', false);
    },
    applyDiscount: ({ commit }) => {
        commit('SET_DISCOUNT_CODE_APPLIED', true);
    },
    reloadDiscount: async ({ commit }) => {
        let discountCodeObject = JSON.parse(
            localStorage.getItem('discountCode')
        );
        let discountApplied = JSON.parse(
            localStorage.getItem('discountApplied')
        );
        if (discountCodeObject && discountApplied !== null) {
            commit('SET_DISCOUNT_CODE_APPLIED', discountApplied);
            commit('SET_DISCOUNT_CODE', discountCodeObject);
        }
    },
    clearCurrentCartItem: ({ commit }) => { },
    clearPaymentDetails: ({ commit }) => {
        commit('SET_PAYMENT_DETAILS', {});
    },
    updatePaymentDetails: ({ commit, state }, { paymentDetails }) => {
        commit('SET_PAYMENT_DETAILS', {
            ...state.paymentDetails,
            ...paymentDetails,
        });
    },
    fetchShippingCountries: async ({ commit, state }) => {
        try {
            if (Object.entries(state.availableShippingCountries).length === 0) {
                const response = await axios.get(
                    `${API_URL}/shipping/countries`
                );
                await commit(
                    'SET_AVAILABLE_SHIPPING_COUNTRIES',
                    response.data.shippingCountries
                );
            }
        } catch (e) {
            console.log(e);
        }
    },
    createOrder: async ({ commit, state, dispatch, rootGetters }) => {
        const postData = {
            cartId: state.shoppingCartID,
            ...state.paymentDetails,
            isBusinessCustomer:
                rootGetters.userPreferences.isBusinessCustomer.value,
        };

        if (state.discountApplied && state.discountCodeObj._id) {
            postData.discountId = state.discountCodeObj._id;
        }

        const response = await $axios.post(
            `${API_URL}/checkout/order/checkout`,
            postData,
            {
                headers: {
                    'x-api-key': process.env.VUE_APP_FULLFILLMENT_API_KEY,
                },
                withCredentials: true,
            }
        );
        await dispatch('clearCart');
        if (
            state.paymentDetails.billingPaymentMethod === 'paypal' &&
            response.data.payment
        ) {
            window.location = response.data.payment;
        } else {
            dispatch('setOrderId', response.data.order._id);
        }
    },
};

const getters = {
    allCartItems: (state, getters) => {
        return state.cartItems.map(cartItem => {
            let item = {
                ...cartItem,
                attachments: cartItem.attachments.map(
                    attachment => `${API_URL}/checkout/cart${attachment}`
                ),
                pricing: calculateTaxes(
                    cartItem.priceCents / 100,
                    getters.taxRate
                ),
                priceCents:
                    cartItem.priceCents * ((getters.taxRate + 100) / 100),
            };
            if (
                state.discountApplied &&
                state.discountCodeObj.type === 'percentage'
            ) {
                item.beforeDiscount = calculateTaxes(
                    cartItem.priceCents / 100,
                    getters.taxRate
                );
                item.pricing = calculateTaxes(
                    calculateDiscount(
                        cartItem.priceCents / 100,
                        state.discountCodeObj.value
                    ),
                    getters.taxRate
                );
            }
            return item;
        });
    },
    allCartItemsSum: state => {
        let allCartItemsSum = 0;
        state.cartItems.forEach(cartItem => {
            allCartItemsSum += cartItem['priceCents'];
        });
        return allCartItemsSum;
    },

    cartSum: state => {
        return state.cartSum;
    },
    taxRate: (state, rootGetters) => {
        try {
            let country;
            /** Billing Country is Selected */
            if (
                state.paymentDetails.billingAddress &&
                state.paymentDetails.billingAddress.countryId.length > 0 &&
                state.availableShippingCountries.length
            ) {
                country = state.availableShippingCountries.find(
                    country =>
                        country._id ===
                        state.paymentDetails.billingAddress.countryId
                );
            } else if (
                /** Billing Country is known from user details */
                rootGetters.userAccountDetails &&
                rootGetters.userAccountDetails.billingInformation &&
                rootGetters.userAccountDetails.billingInformation.countryId
                    .length > 0 &&
                state.availableShippingCountries.length
            ) {
                country = state.availableShippingCountries.find(
                    country =>
                        country._id ===
                        rootGetters.userAccountDetails.billingInformation
                            .countryId
                );
            } else {
                return 19;
            }

            if (country.countryCode !== 'DE') {
                if (
                    rootGetters.userPreferences.isBusinessCustomer.value ||
                    !country.isEU
                ) {
                    return 0;
                } else {
                    return country.taxRate;
                }
            }

            return country.taxRate;
        } catch (e) {
            return 19;
        }

        /** Unknown Customer */
        return 19;
    },
    positionPrice: (state, getters) => {
        let cumulatedPrice = {
            netPrice: 0,
            totalPrice: 0,
            tax: 0,
            taxRate: getters.taxRate,
        };

        state.cartItems.forEach(item => {
            let itemTax = calculateTaxes(
                item.priceCents / 100,
                getters.taxRate
            );
            cumulatedPrice.tax += itemTax.tax;
            cumulatedPrice.netPrice += itemTax.netPrice;
        });

        let positionPrice = {
            ...cumulatedPrice,
            totalPrice: cumulatedPrice.netPrice + cumulatedPrice.tax,
        };

        if (
            state.discountApplied &&
            state.discountCodeObj.type === 'percentage'
        ) {
            positionPrice = calculateTaxes(
                calculateDiscount(
                    cumulatedPrice.netPrice,
                    state.discountCodeObj.value
                ),
                getters.taxRate
            );
            positionPrice.beforeDiscount = calculateTaxes(
                cumulatedPrice.netPrice,
                getters.taxRate
            );
        }

        if (
            state.discountApplied &&
            state.discountCodeObj.type === 'absolute'
        ) {
            positionPrice = calculateTaxes(
                cumulatedPrice.netPrice - state.discountCodeObj.value,
                getters.taxRate
            );
            positionPrice.beforeDiscount = calculateTaxes(
                cumulatedPrice.netPrice,
                getters.taxRate
            );
        }

        return positionPrice;
    },
    cumulatedProductionTime: state => {
        return Math.max(...state.cartItems.map(item => item.productionTime));
    },
    cartContainsAttachedDrawings: state => {
        return !state.cartItems.every(
            item => item.attachments[0] === undefined
        );
    },
    cartItemByID: (state, getters) => cartItemID => {
        let cartItem = state.cartItems.find(
            cartItem => cartItem._id === cartItemID
        );
        if (cartItem) {
            let item = {
                ...cartItem,
                thumbnail: `${API_URL}${cartItem.thumbnail}`,
                attachments: cartItem.attachments.map(
                    attachement => `${API_URL}/checkout/cart${attachement}`
                ),
                pricing: calculateTaxes(
                    cartItem.priceCents / 100,
                    getters.taxRate
                ),
                priceCents:
                    cartItem.priceCents * ((getters.taxRate + 100) / 100),
            };

            if (
                state.discountApplied &&
                state.discountCodeObj.type === 'percentage'
            ) {
                item.beforeDiscount = calculateTaxes(
                    item.priceCents / 100,
                    getters.taxRate
                );
                item.pricing = calculateTaxes(
                    calculateDiscount(
                        item.priceCents / 100,
                        state.discountCodeObj.value
                    ),
                    getters.taxRate
                );
            }
            return item;
        }
    },
    currentCartItem: state => {
        return state.currentCartItem;
    },
    paymentDetails: state => {
        return state.paymentDetails;
    },
    shippingMethod: state => {
        return Object.entries(state.shippingMethod).length > 0
            ? state.shippingMethod
            : null;
    },
    availablePaymentMethods: state => {
        return state.availablePaymentMethods;
    },
    availableShippingCountries: state => {
        return state.availableShippingCountries.sort((a, b) =>
            a.country.localeCompare(b.country)
        );
    },
    shippingCountryById: state => id => {
        return Object.values(state.availableShippingCountries).find(
            country => country._id === id
        );
    },
    discount: (state, getters) => {
        let discount = state.discountCodeObj;

        if (discount.type === 'absolute') {
            discount.calculatedTaxes = calculateTaxes(
                discount.value,
                getters.taxRate
            );
        }
        return {
            ...discount,
        };
    },
    discountApplied: state => {
        return state.discountApplied;
    },
};

export default { state, mutations, actions, getters };
